import { Component, computed, effect, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';

import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';

import { AuthService } from './core/auth/auth.service';
import { PortfolioState } from './core/portfolio-state/portfolio-state';
import { ThemeService } from './core/theme/theme.service';
import { ManagePortfoliosDialog } from './features/portfolios/manage-portfolios-dialog';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  protected readonly themeService = inject(ThemeService);
  protected readonly portfolioState = inject(PortfolioState);

  protected readonly user = this.auth.user;
  protected readonly isDark = this.themeService.isDark;

  protected readonly selectedPortfolioName = computed(() => {
    const selected = this.portfolioState.selectedPortfolio();
    return selected ? selected.name : 'All Portfolios';
  });

  constructor() {
    effect(() => {
      const u = this.user();
      if (u) {
        this.portfolioState.loadPortfolios();
      } else {
        this.portfolioState.reset();
      }
    });
  }

  protected selectPortfolio(id: string): void {
    this.portfolioState.selectPortfolio(id);
  }

  protected openManagePortfolios(): void {
    this.dialog.open(ManagePortfoliosDialog, {
      width: '450px',
    });
  }

  protected readonly avatarError = signal(false);
  protected readonly photoUrl = computed(() => this.user()?.photoURL ?? null);
  protected readonly userEmail = computed(() => this.user()?.email ?? '');
  protected readonly userInitial = computed(() =>
    (this.userEmail().charAt(0) || 'U').toUpperCase(),
  );

  protected toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  protected onAvatarError(): void {
    this.avatarError.set(true);
  }

  protected async signOut(): Promise<void> {
    await this.auth.signOut();
    await this.router.navigate(['/login']);
  }
}
